import React, { FC, useCallback, useMemo, useState } from 'react';
import { IonContent, IonPage, useIonViewWillEnter, useIonViewWillLeave } from '@ionic/react';
import { ChevronHeader } from '../../../components/ChevronHeader';
import { Container, ReSchedule, ReScheduleSuccess } from '@mindly/ui-components';
import { RouteComponentProps, useHistory } from 'react-router';
import { PrivateRoutes, PublicRoutes } from '../../../routes/navigation';
import { useDispatch, useSelector } from 'react-redux';
import { consultationsByIDSelector } from '../../../redux/consultations/selector';
import { DateTime } from 'luxon';
import { setIsActive } from '../../../redux/tabBar/reducer';
import { updateTime } from 'src/redux/consultations/reducer';
import { RescheduleWrapper } from './RescheduleConfirmPage.style';
import { getChatRoomsListBySpecialistId } from '../../../api/agora/chatRooms';
import { ChatRoomsData } from '../../../redux/chatRooms/types';
import { SystemMessagesType, UserType } from '../../types';
import useAgoraRtm from '../../../context/AgoraContext/AgoraContext';
import { userSelector } from '../../../redux/user/selector';
import { consultationTimeToFormatSeconds } from '../../SchedulePage/utils';
import useLangs from '../../../context/LangsContext';
import { useIntl } from 'react-intl';
import { formatSystemMessage } from '../../../context/AgoraContext/formatSystemMessage';
import EmailSystem from '../../../api/emails';
import { rescheduleSession } from '../../../api/consultation';
import { psychologistSelectorById } from '../../../redux/specialists/selector';
import useToast from '../../../context/ToastContext/ToastContext';

interface RescheduleConfirmPageProps
  extends RouteComponentProps<{
    sessionID: string;
    time: string;
  }> {}

const RescheduleConfirmPage: FC<RescheduleConfirmPageProps> = ({ match }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { locale } = useLangs();
  const intl = useIntl();

  const { sessionID, time } = match.params;

  const { sendSystemMessage } = useAgoraRtm();

  const sessionData = useSelector(consultationsByIDSelector(sessionID));
  const currentSpecialist = useSelector(psychologistSelectorById(sessionData?.psychologist_id ?? ''));
  const currentUser = useSelector(userSelector);

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useIonViewWillEnter(() => {
    if (!currentUser?.id) {
      history.push(PublicRoutes.AUTH);
      return;
    }
    dispatch(setIsActive(false));
    if (!sessionData) {
      history.push(PrivateRoutes.CONSULTATION);
    }
  });

  useIonViewWillLeave(() => {
    setIsSuccess(false);
    dispatch(setIsActive(true));
  });

  const handleReschedule = useCallback(
    async (reason: string) => {
      try {
        if (!sessionData?.id) return;

        setIsLoading(true);
        const reschedulingDate = DateTime.fromSeconds(Number(time)).toUTC().toISO();

        await rescheduleSession(sessionID, reschedulingDate);

        const { data: roomData } = await getChatRoomsListBySpecialistId(
          sessionData.user_id,
          sessionData.psychologist_id
        );
        await sendSystemMessage({
          roomData: roomData?.[0] as ChatRoomsData,
          specialistId: sessionData.psychologist_id,
          user: currentUser as Omit<UserType, 'id'> & { id: string },
          message: formatSystemMessage(
            SystemMessagesType.session_rescheduled,
            {
              userName: currentUser?.display_name ?? '',
              formattedOldConsultationTime: consultationTimeToFormatSeconds(sessionData.consultation_time),
              formattedNewConsultationTime: time,
              reason,
            },
            intl
          ),
          notifyMessage: intl.formatMessage(
            {
              id: 'booking.notify_message.reschedule',
              defaultMessage: 'Перенес(ла) сеанс з {timeOld} на {timeNew}',
            },
            {
              timeOld: DateTime.fromSeconds(consultationTimeToFormatSeconds(sessionData.consultation_time))
                .setZone(currentSpecialist?.time_zone ?? 'UTC')
                .setLocale(locale)
                .toFormat('dd MMMM HH:mm'),
              timeNew: DateTime.fromSeconds(+time)
                .setZone(currentSpecialist?.time_zone ?? 'UTC')
                .setLocale(locale)
                .toFormat('dd MMMM HH:mm'),
            }
          ),
          locale,
        });

        EmailSystem.sendSessionRescheduledOrCanceled('reschedule')(sessionID, reason);
        dispatch(updateTime({ id: sessionID, time: Number(time) }));
        setIsSuccess(true);
      } catch (error: any) {
        showToast({ error });
      } finally {
        setIsLoading(false);
      }
    },
    [sessionData, sessionID, time, currentUser, currentSpecialist?.time_zone]
  );

  const specialistName = useMemo(
    () => `${sessionData?.psychologist_name.split(' ')[0]} ${sessionData?.psychologist_name.split(' ')[1][0]}.`,
    [sessionData]
  );

  return (
    <IonPage>
      <ChevronHeader
        title={intl.formatMessage({
          id: 'bocking.title',
          defaultMessage: 'Вибери час',
        })}
        subTitle={intl.formatMessage(
          { id: 'bocking.step', defaultMessage: 'Крок {from} із {to}' },
          {
            from: 2,
            to: 2,
          }
        )}
      />
      {isSuccess ? (
        <ReScheduleSuccess
          name={specialistName}
          avatar={sessionData?.psychologist_avatar ?? ''}
          time={Number(time)}
          timeZone={DateTime.local().zoneName}
          onClick={() => {
            history.push(PrivateRoutes.CONSULTATION);
          }}
          locale={locale}
          success={intl.formatMessage({
            id: 'general.btn.success',
            defaultMessage: 'Готово',
          })}
          cancelTitle={intl.formatMessage({
            id: 'booking.cancel.title',
            defaultMessage: 'Сеанс скасовано',
          })}
          successTitle={intl.formatMessage({
            id: 'booking.reschedule.title',
            defaultMessage: 'Сеанс перенесено на',
          })}
        />
      ) : (
        <IonContent>
          <Container>
            {sessionData && (
              <RescheduleWrapper>
                <ReSchedule
                  name={specialistName}
                  avatar={sessionData.psychologist_avatar}
                  time={Number(time)}
                  timeZone={DateTime.local().zoneName}
                  isLoading={isLoading}
                  onSubmit={handleReschedule}
                  emptyReasonErrorMessage={intl.formatMessage({
                    id: 'booking.cancel_session.emptyReasonErrorMessage',
                    defaultMessage: 'Щоб продовжити, будь ласка, надайте коментар.',
                  })}
                  locale={locale}
                  translations={{
                    title: intl.formatMessage({
                      id: 'reschedule.modal.title',
                      defaultMessage: 'Підтверди новий час',
                    }),
                    placeholder: intl.formatMessage({
                      id: 'reschedule.modal.placeholder',
                      defaultMessage: 'Додай коментар для психолога...',
                    }),
                    btnLabel: intl.formatMessage({
                      id: 'reschedule.modal.btn',
                      defaultMessage: 'Підтвердити новий час',
                    }),
                  }}
                />
              </RescheduleWrapper>
            )}
          </Container>
        </IonContent>
      )}
    </IonPage>
  );
};

export default RescheduleConfirmPage;
